import { createAsyncThunk, createSlice, GetThunkAPI, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { ApiFactory } from "../../api";
import { IDocumentMetadataApi } from "../../api/document-metadata-v2";
import { IDocumentApi } from "../../api/document-v2";
import { MetadataFactory, WaardelijstFactory } from "../../factories";
import { DocumentMetadata, Identifier } from "../../models";
import {
  Bestand,
  Documenthandelingen,
  Documentrelaties,
  PUBLICATIE_STATUS,
  Velden,
} from "../../models/document-metadata";
import { IdentifierMetAfkorting } from "../../models/identifier";
import { PUBLICATIE_STATUS_SEARCH, SearchResult } from "../../models/search-result";
import { ChangeFile } from "../../ui/interfaces/changeFile";
import { DocumentError } from "../../ui/interfaces/documentError";
import { stringToDate } from "../../utils/DateFormatter";
import { identifierMetChildrenToIdentifier } from "../../utils/Metadata";
import { ReducerErrorToaster } from "../../utils/ReducerUtils";
import { BIJLAGE_VAN, getHoofdDocRelatie } from "../../utils/Relaties";
import { InsertOrRemoveIdentifier, removeEmpty } from "../../utils/StateHelpers";
import { StringSanitizer } from "../../utils/StringSanitizer";
import { RequiredListValidator } from "../../validations/requiredValidator";
import {
  BijlageContext,
  CreateNewFormArgs,
  ProcessFormUpdateArgs,
  UpdateFilesArgs,
  UpdateMetadataArgs,
} from "./document-metadata-interfaces";
import { setToast } from "./toast";

export const conceptDate = new Date("9999-12-31T12:00:00.000Z");

const mdFactory = new MetadataFactory();
const stringSanitizer = new StringSanitizer();

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error("Onbekende error"));

export interface MetadataState {
  data: FormState[][];
  reasons: {
    pid: string;
    reasonId: string;
  }[];
  setIndex: number;
  formIndex: number;
  fetching: boolean;
  mode: "create" | "update";
  formUniqueKey: number;
  documentError: {
    pid?: string[];
    indexList?: { index: number; setIndex: number }[];
  };
  errorReviewed: boolean;
  localValidationError: boolean;
}

export interface FormState {
  file?: File;
  hasChangedMeta?: boolean;
  hasChangedFile?: boolean;
  fileExplicitlyRemoved?: boolean;
  originalWeblocatieUrl?: string;
  meta: DocumentMetadata;
}

export interface CancelFileChangePayload {
  weblocatieVoorWijziging?: string;
  hasChangedMetaVoorWijziging?: boolean;
  bestandenVoorWijziging?: Bestand[];
}

const initialState: MetadataState = {
  data: [],
  reasons: [],
  fetching: false,
  mode: "create",
  setIndex: 0,
  formIndex: 0,
  formUniqueKey: 0,
  documentError: {},
  errorReviewed: false,
  localValidationError: false,
};

const getHoofddocumentId = (state: MetadataState, setIndex: number, index: number): string | undefined => {
  if (index === 0) return undefined;
  const pid = state.data[setIndex][0].meta.document.pid;
  return pid ? stringSanitizer.extractIdentifierFromPid(pid) : undefined;
};

const generateMetadata = (title: string, organization: IdentifierMetAfkorting) => {
  return mdFactory.create({
    publisher: {
      id: organization.id,
      label: organization.label,
    },
    identifiers: [],
    language: {
      id: "http://publications.europa.eu/resource/authority/language/NLD",
      label: "Nederlands",
    },
    titelcollectie: {
      officieleTitel: title,
    },
    classificatiecollectie: {
      documentsoorten: [],
      informatiecategorieen: [],
      themas: [],
    },
    documenthandelingen: [
      {
        soortHandeling: {
          id: "https://identifier.overheid.nl/tooi/def/thes/kern/c_641ecd76",
          label: "vaststelling",
        },
        atTime: new Date().toJSON(),
        wasAssociatedWith: {
          id: organization.id,
          label: organization.label,
        },
      },
    ],
    publicatiebestemming: ["rijksoverheid.nl"],
    verantwoordelijke: {
      id: organization.id,
      label: organization.label,
    },
  });
};

const validateForm = (data: FormState, validateFile: boolean): string | undefined => {
  const weblocatieEnabled = process.env.REACT_APP_WEBLOCATIE === "true";
  const hasFile = data.file;
  const hasWeblocatie = weblocatieEnabled && Boolean(data.meta.document.weblocatie);
  if ((validateFile || data.hasChangedFile) && !hasFile && !hasWeblocatie) return "Bestand ontbreekt.";

  if (validatePublisherFields(data)) {
    return "Publicerende organisatie is niet geldig.";
  }

  if (!validateRequiredFields(data)) {
    return "Verplichte velden niet ingevuld.";
  }

  if (!validateFormTextInputs(data)) return "Minimaal 3 karakters vereist.";
};

const validateRequiredFields = (data: FormState): boolean => {
  let requiredValues: (string | Identifier[])[] = [
    data.meta.document.publisher.id,
    data.meta.document.titelcollectie.officieleTitel,
    data.meta.document.identifiers[0],
    data.meta.document.language.id,
  ];

  if (process.env.REACT_APP_INFORMATIECATEGORIE === "true")
    requiredValues.push(data.meta.document.classificatiecollectie.informatiecategorieen);

  if (data.meta.document.publicatiebestemming !== undefined && data.meta.document.publicatiebestemming.length > 0)
    if (
      new RequiredListValidator().validate(
        data.meta.document.classificatiecollectie.documentsoorten,
        identifierMetChildrenToIdentifier(new WaardelijstFactory().createDocumentsoortenLijstKST(false))
      )
    )
      return false;

  return requiredValues.every((rv: string | Identifier[]) => rv && rv.length > 0);
};

const validatePublisherFields = (data: FormState): boolean => {
  return data.meta.document.publisher.label!.includes("(verlopen)");
};

const validateFormTextInputs = (data: FormState): boolean => {
  const minChar: number = 3;

  const {
    titelcollectie: { officieleTitel, verkorteTitels, alternatieveTitels },
    naamOpsteller,
    aggregatiekenmerk,
    onderwerpen,
    identifiers,
    omschrijvingen,
    creatiedatum,
  } = data.meta.document;

  let textValuesRequired = [identifiers, officieleTitel];

  let textValuesOptional = [
    verkorteTitels,
    alternatieveTitels,
    aggregatiekenmerk,
    onderwerpen,
    omschrijvingen,
    creatiedatum,
    naamOpsteller,
  ];

  const textValuesRequiredIncorrect = textValuesRequired.filter(
    (tekst: string | string[] | undefined) =>
      tekst !== undefined &&
      tekst.length > 0 &&
      !(Array.isArray(tekst) ? tekst.every((arrayValue) => arrayValue.length >= minChar) : tekst.length >= minChar)
  );
  const textValuesOptionalIncorrect = textValuesOptional.filter(
    (tekst: string | string[] | undefined) =>
      tekst !== undefined &&
      !(Array.isArray(tekst)
        ? tekst.every((arrayValue) => arrayValue.length >= minChar || arrayValue.length === 0)
        : tekst.length >= minChar || tekst.length === 0)
  );
  return textValuesRequiredIncorrect.length === 0 && textValuesOptionalIncorrect.length === 0;
};

const UpdateErrorsSetFormIndex = (thunkAPI: any, errorsArray: { index: number; setIndex: number }[]) => {
  thunkAPI.dispatch(updateErrors(errorsArray));
  thunkAPI.dispatch(changeFormIndex({ index: errorsArray[0].index, setIndex: errorsArray[0].setIndex }));
};

const getBijlagenRelatiesVanHoofdDoc = async (
  thunkAPI: GetThunkAPI<any>,
  api: IDocumentMetadataApi,
  rel_pid: string
): Promise<Documentrelaties[]> => {
  const hoofdDoc = await fetchMetaForId(thunkAPI, api, rel_pid);
  thunkAPI.dispatch(_addForm({ meta: hoofdDoc }));
  return hoofdDoc.documentrelaties ?? [];
};

const getBijlagenRelatiesEnSetForm = async (
  thunkAPI: GetThunkAPI<any>,
  api: IDocumentMetadataApi,
  meta: DocumentMetadata
): Promise<Documentrelaties[]> => {
  const hoofdDocRelatie = getHoofdDocRelatie(meta.documentrelaties ?? []);

  if (!hoofdDocRelatie) {
    thunkAPI.dispatch(_addForm({ meta }));
    return meta.documentrelaties ?? [];
  }

  const rel_pid = stringSanitizer.extractIdentifierFromPid(hoofdDocRelatie);
  if (!rel_pid) return [];

  return getBijlagenRelatiesVanHoofdDoc(thunkAPI, api, rel_pid);
};

const informatiecategorieenToggleCheck = (state: MetadataState) => {
  if (process.env.REACT_APP_INFORMATIECATEGORIE === "false") {
    let nieuweState: any = { ...state };
    const modifiedItems = nieuweState.data.map((dataItem: any) => {
      let nieuwMetaItem = JSON.parse(JSON.stringify(dataItem.meta));
      delete nieuwMetaItem.document.classificatiecollectie.informatiecategorieen;
      return { ...dataItem, meta: nieuwMetaItem };
    });
    return { ...state, data: modifiedItems };
  } else {
    return state;
  }
};

const collectCreateErrors = (listOfSets: FormState[][]): DocumentError[] => {
  const errors: DocumentError[] = [];
  listOfSets.forEach((set: FormState[], setIndex: number) => {
    set.forEach((formState: FormState, index: number) => {
      const message = validateForm(formState, true);
      if (message !== undefined) errors.push({ index, setIndex, message });
    });
  });
  return errors;
};

const checkErrors = (
  data: FormState[],
  createNew: boolean
): {
  index: number;
  message: string | undefined;
}[] => {
  return data
    .map((formState: FormState, index: number) => {
      if (formState.meta.document.pid === undefined && !createNew) return { index: index, message: undefined };
      if (formState.meta.document.pid !== undefined)
        return { index: index, message: validateForm(formState, !!formState.fileExplicitlyRemoved) };
      return { index: index, message: validateForm(formState, true) };
    })
    .filter((error: DocumentError) => error?.message !== undefined);
};

const updateError = (thunkAPI: any, errorList: { index: number; message: string | undefined }[]) => {
  if (errorList.length > 0) {
    const error = errorList[0];
    thunkAPI.dispatch(changeFormIndex({ index: error.index, setIndex: 0 }));
    const errorMessage = new Error(error.message);
    ReducerErrorToaster(thunkAPI, errorMessage);
  }
};

const updateMetadata = async ({
  thunkAPI,
  metadata_api,
  _form,
  errorIndexList,
  errorList,
  pid,
  index,
  redenId,
  hoofddocumentId,
}: UpdateMetadataArgs): Promise<boolean> => {
  if (!_form.hasChangedMeta) return true;

  try {
    const resultMeta = await metadata_api.updateMetadata(_form.meta, pid, redenId, hoofddocumentId);
    if (resultMeta) thunkAPI.dispatch(updateBewerktijd(index));
    return true;
  } catch (error: unknown) {
    errorIndexList.push({ index: index, setIndex: 0 });
    thunkAPI.dispatch(changeFormIndex({ index, setIndex: 0 }));
    errorList.push({ index, message: toError(error).message });
    return false;
  }
};

const updateFiles = async ({
  thunkAPI,
  document_api,
  _form,
  errorIndexList,
  errorList,
  pid,
  reasonId,
  index,
  hoofddocumentId,
}: UpdateFilesArgs) => {
  if (!_form.file || !_form.hasChangedFile) return;

  try {
    await document_api.updateDocument(pid, _form.file, reasonId ?? "", hoofddocumentId);
    thunkAPI.dispatch(removeFile(index));
  } catch (error: unknown) {
    errorIndexList.push({ index: index, setIndex: 0 });
    errorList.push({ index: index, message: toError(error).message });
    thunkAPI.dispatch(changeFormIndex({ index: index, setIndex: 0 }));
  }
};

const updateErrorsOfSuccesvol = (
  thunkAPI: any,
  errorIndexList: { index: number; setIndex: number }[],
  errorList: { index: number; message: string | undefined }[],
  hasUpdateAction: boolean,
  existingErrorIndexList: { index: number; setIndex: number }[]
) => {
  if (errorIndexList.length > 0) {
    const firstError = errorList[0];
    ReducerErrorToaster(thunkAPI, new Error(firstError.message));
    UpdateErrorsSetFormIndex(thunkAPI, errorIndexList);
    throw new Error(firstError.message);
  }

  if (hasUpdateAction) {
    thunkAPI.dispatch(updateErrors([]));
  } else if (existingErrorIndexList.length > 0) {
    thunkAPI.dispatch(updateErrors(existingErrorIndexList));
  }

  thunkAPI.dispatch(clearReasons());

  if (hasUpdateAction) {
    thunkAPI.dispatch(
      setToast({
        autoClose: true,
        type: "success",
        message: {
          message: "Document(en) aangeleverd.",
        },
      })
    );
  }
};

const createNewForm = async ({
  thunkAPI,
  metadata_api,
  document_api,
  errorIndexList,
  errorList,
  setIndexValue,
  i,
  createNew,
  form,
  hoofddocumentId,
}: CreateNewFormArgs): Promise<boolean> => {
  if (!createNew) return false;

  let createResult: { uploadUrl: string; pid: string };
  try {
    createResult = await metadata_api.createMetadataSession(form.meta, hoofddocumentId);
  } catch (error: unknown) {
    const message = toError(error).message;
    errorIndexList.push({ index: i, setIndex: setIndexValue });
    errorList.push({ index: i, message });
    ReducerErrorToaster(thunkAPI, new Error(message));
    UpdateErrorsSetFormIndex(thunkAPI, errorIndexList);
    throw error;
  }

  let uploadFailed = false;

  if (!form.meta.document.weblocatie) {
    try {
      await document_api.uploadDocument(createResult.uploadUrl, form.file as File);
    } catch (error: unknown) {
      uploadFailed = true;
      errorIndexList.push({ index: i, setIndex: setIndexValue });
      errorList.push({ index: i, message: toError(error).message });
    }
  }

  thunkAPI.dispatch(updateNewBijlage({ index: i, pid: createResult.pid, fileUploadFailed: uploadFailed }));
  return true;
};

const processFormUpdate = async ({
  thunkAPI,
  metadata_api,
  document_api,
  state,
  errorIndexList,
  errorList,
  setIndexValue,
  i,
  createNew,
}: ProcessFormUpdateArgs): Promise<boolean> => {
  const form = state.data[setIndexValue][i];
  const isNew = form.meta.document.pid === undefined;
  const hoofddocumentId = getHoofddocumentId(state, setIndexValue, i);

  if (isNew) {
    return createNewForm({
      thunkAPI,
      metadata_api,
      document_api,
      errorIndexList,
      errorList,
      setIndexValue,
      i,
      createNew,
      form,
      hoofddocumentId,
    });
  }

  if (!form.hasChangedMeta && !form.hasChangedFile) {
    return false;
  }

  const pid = stringSanitizer.extractIdentifierFromPid(form.meta.document.pid);
  const reasonId = state.reasons.find((wijzig: ChangeFile) => wijzig.pid === form.meta.document.pid)?.reasonId;
  const metaRedenId = form.hasChangedFile && form.file ? undefined : reasonId;

  const metadataOk = await updateMetadata({
    thunkAPI,
    metadata_api,
    _form: form,
    errorIndexList,
    errorList,
    pid,
    index: i,
    redenId: metaRedenId,
    hoofddocumentId,
  });

  if (metadataOk) {
    await updateFiles({
      thunkAPI,
      document_api,
      _form: form,
      errorIndexList,
      errorList,
      pid,
      reasonId,
      index: i,
      hoofddocumentId,
    });
  }
  return true;
};

const markBijlageError = (
  thunkAPI: any,
  index: number,
  setIndex: number,
  pid: string,
  error: unknown,
  errorIndexList: { index: number; setIndex: number }[],
  errorMessages: string[]
) => {
  errorIndexList.push({ index, setIndex });
  errorMessages.push(toError(error).message);
  thunkAPI.dispatch(_updateBijlageRelatie({ index, pid }));
  thunkAPI.dispatch(_updateHasChanged({ index, hasChanged: true }));
  ReducerErrorToaster(thunkAPI, toError(error));
};

const uploadBijlageDocument = async (
  context: BijlageContext,
  document_api: IDocumentApi,
  _bijlage: FormState,
  createBijlageResult: { uploadUrl: string; pid: string }
): Promise<boolean> => {
  if (_bijlage.meta.document.weblocatie) return false;

  try {
    await document_api.uploadDocument(createBijlageResult.uploadUrl, _bijlage.file as File);
    return false;
  } catch (error: unknown) {
    const { thunkAPI, index, setIndex, createResultPid, errorIndexList, errorMessages } = context;
    markBijlageError(thunkAPI, index, setIndex, createResultPid, error, errorIndexList, errorMessages);
    return true;
  }
};

const createBijlage = async (
  context: BijlageContext,
  bijlage: FormState,
  metadata_api: IDocumentMetadataApi,
  document_api: IDocumentApi
): Promise<void> => {
  const { thunkAPI, index, setIndex, createResultPid, errorIndexList, errorMessages } = context;

  const _bijlage: FormState = {
    ...bijlage,
    meta: {
      ...bijlage.meta,
      documentrelaties: [
        {
          relation: stringSanitizer.extractIdentifierFromPid(createResultPid),
          role: BIJLAGE_VAN,
        },
      ],
    },
  };

  let createBijlageResult: { uploadUrl: string; pid: string } | undefined;
  try {
    createBijlageResult = await metadata_api.createMetadataSession(
      _bijlage.meta,
      stringSanitizer.extractIdentifierFromPid(createResultPid)
    );
  } catch (error: unknown) {
    markBijlageError(thunkAPI, index, setIndex, createResultPid, error, errorIndexList, errorMessages);
    return;
  }

  const uploadFailed = await uploadBijlageDocument(context, document_api, _bijlage, createBijlageResult);

  thunkAPI.dispatch(updatePid({ pid: createBijlageResult.pid, index }));

  if (!uploadFailed) {
    thunkAPI.dispatch(_updateHasChanged({ index, hasChanged: false }));
  }
};

const createBijlagen = async (
  state: MetadataState,
  createResult: { uploadUrl: string; pid: string },
  metadata_api: IDocumentMetadataApi,
  document_api: IDocumentApi,
  thunkAPI: any
): Promise<{ errorIndexList: { index: number; setIndex: number }[]; errorMessages: string[] }> => {
  const errorIndexList: { index: number; setIndex: number }[] = [];
  const errorMessages: string[] = [];
  const setIndexValue = state.setIndex;

  for (let i = 1; i < state.data[0].length; i++) {
    const context: BijlageContext = {
      thunkAPI,
      index: i,
      setIndex: setIndexValue,
      createResultPid: createResult.pid,
      errorIndexList,
      errorMessages,
    };
    await createBijlage(context, state.data[0][i], metadata_api, document_api);
  }

  return { errorIndexList, errorMessages };
};

const validateCreateErrors = (thunkAPI: any, state: MetadataState): DocumentError[] => {
  const errorList = collectCreateErrors(state.data);

  if (errorList.length > 0) {
    const errorIndexList = errorList.map((e) => ({ index: e.index, setIndex: e.setIndex ?? 0 }));
    const error = new Error(errorList[0].message);
    UpdateErrorsSetFormIndex(thunkAPI, errorIndexList);
    ReducerErrorToaster(thunkAPI, error);
    thunkAPI.dispatch(updateLocalValidationError(true));
    throw error;
  }

  return errorList;
};

const createHoofdDocumentSession = async (
  thunkAPI: any,
  metadata_api: IDocumentMetadataApi,
  state: MetadataState,
  hoofd_document: FormState
): Promise<{ uploadUrl: string; pid: string }> => {
  try {
    return await metadata_api.createMetadataSession(hoofd_document.meta);
  } catch (error: unknown) {
    UpdateErrorsSetFormIndex(thunkAPI, [{ index: 0, setIndex: state.setIndex }]);
    ReducerErrorToaster(thunkAPI, toError(error));
    throw error;
  }
};

const uploadHoofdDocument = async (
  thunkAPI: any,
  document_api: IDocumentApi,
  state: MetadataState,
  hoofd_document: FormState,
  createResult: { uploadUrl: string; pid: string }
): Promise<void> => {
  let hoofdUploadError: Error | undefined;

  if (!hoofd_document.meta.document.weblocatie) {
    try {
      await document_api.uploadDocument(createResult.uploadUrl, hoofd_document.file as File);
    } catch (error: unknown) {
      hoofdUploadError = toError(error);
    }
  }

  thunkAPI.dispatch(updateErrorPid(createResult.pid));
  thunkAPI.dispatch(updatePid({ pid: createResult.pid, index: 0 }));
  thunkAPI.dispatch(updateErrorReviewed(false));
  thunkAPI.dispatch(_updateHasChanged({ index: 0, hasChanged: !!hoofdUploadError }));

  if (hoofdUploadError) {
    UpdateErrorsSetFormIndex(thunkAPI, [{ index: 0, setIndex: state.setIndex }]);
    ReducerErrorToaster(thunkAPI, hoofdUploadError);
    throw hoofdUploadError;
  }
};

const finalizeCreate = (
  thunkAPI: any,
  errorIndexList: { index: number; setIndex: number }[],
  errorList: DocumentError[],
  bijlageErrorMessages: string[],
  createResult: { uploadUrl: string; pid: string }
): void => {
  if (errorIndexList.length > 0) {
    UpdateErrorsSetFormIndex(thunkAPI, errorIndexList);
    const pid = stringSanitizer.extractIdentifierFromPid(createResult.pid);
    const newURL = new URL(globalThis.location.href + "/" + pid);
    globalThis.location.href = newURL.toString();
    throw new Error(errorList[0]?.message ?? bijlageErrorMessages[0] ?? "Bijlage(n) konden niet worden aangeleverd.");
  }

  thunkAPI.dispatch(
    setToast({
      autoClose: true,
      type: "success",
      message: {
        message: "Document(en) aangeleverd.",
      },
    })
  );

  thunkAPI.dispatch(clearDocumentState());
  thunkAPI.dispatch(addForm());
};

export const create = createAsyncThunk<unknown, undefined, { state: RootState }>(
  "metadata/create",
  async (_, thunkAPI) => {
    const metadata_api = ApiFactory.createDocumentMetadataApi();
    const document_api = ApiFactory.createDocumentApi();

    let state: MetadataState = thunkAPI.getState().meta;
    const hoofd_document: FormState = state.data[state.setIndex][0];
    state = informatiecategorieenToggleCheck(state);

    const errorList = validateCreateErrors(thunkAPI, state);
    thunkAPI.dispatch(updateLocalValidationError(false));

    const createResult = await createHoofdDocumentSession(thunkAPI, metadata_api, state, hoofd_document);
    await uploadHoofdDocument(thunkAPI, document_api, state, hoofd_document, createResult);

    const { errorIndexList: bijlageErrorIndexList, errorMessages: bijlageErrorMessages } = await createBijlagen(
      state,
      createResult,
      metadata_api,
      document_api,
      thunkAPI
    );

    finalizeCreate(thunkAPI, bijlageErrorIndexList, errorList, bijlageErrorMessages, createResult);
  },
  {
    condition: (_, { getState }) => !getState().meta.fetching,
  }
);

export const update = createAsyncThunk<unknown, boolean, { state: RootState }>(
  "metadata/update",
  async (createNew: boolean, thunkAPI) => {
    const metadata_api = ApiFactory.createDocumentMetadataApi();
    const document_api = ApiFactory.createDocumentApi();
    let state: MetadataState = thunkAPI.getState().meta;
    const existingErrorIndexList = state.documentError.indexList ?? [];
    let errorIndexList: { index: number; setIndex: number }[] = [];
    let hasUpdateAction = false;

    state = informatiecategorieenToggleCheck(state);
    const errorList = checkErrors(state.data[state.setIndex], createNew);
    errorIndexList = errorList.map((e) => ({ index: e.index, setIndex: 0 }));

    if (errorIndexList.length > 0) {
      thunkAPI.dispatch(updateErrors(errorIndexList));
      updateError(thunkAPI, errorList);
      return;
    }

    const reasonErrorList = state.data[state.setIndex]
      .map((formState: FormState, index: number) => {
        if (formState.meta.document.pid !== undefined && formState.hasChangedFile) {
          const hasReason = state.reasons.some((r: ChangeFile) => r.pid === formState.meta.document.pid && r.reasonId);
          if (!hasReason) return { index, message: "Reden van wijzigen verplicht." };
        }
        return { index, message: undefined };
      })
      .filter((e): e is { index: number; message: string } => e.message !== undefined);

    if (reasonErrorList.length > 0) {
      thunkAPI.dispatch(updateErrors(reasonErrorList.map((e) => ({ index: e.index, setIndex: 0 }))));
      updateError(thunkAPI, reasonErrorList);
      return;
    }

    const setIndexValue = state.setIndex;
    for (let i = 0; i < state.data[0].length; i++) {
      if (errorList?.find((error: DocumentError) => error.index === i)) continue;

      const didUpdate = await processFormUpdate({
        thunkAPI,
        metadata_api,
        document_api,
        state,
        errorIndexList,
        errorList,
        setIndexValue,
        i,
        createNew,
      });

      if (didUpdate) {
        hasUpdateAction = true;
      }

      state = thunkAPI.getState().meta;
    }

    updateErrorsOfSuccesvol(thunkAPI, errorIndexList, errorList, hasUpdateAction, existingErrorIndexList);
  },
  {
    condition: (_, { getState }) => !getState().meta.fetching,
  }
);

export const publiceerConcept = createAsyncThunk(
  "metadata/publiceerConcept",
  async (selectedConcepts: SearchResult[], thunkAPI) => {
    const metadata_api = ApiFactory.createDocumentMetadataApi();

    for (let i = selectedConcepts.length - 1; i >= 0; i--) {
      const _form = selectedConcepts[i];
      const pid = stringSanitizer.extractIdentifierFromPid(_form.document.pid);

      const _newForm: SearchResult = {
        ..._form,
        document: {
          ..._form.document,
          zichtbaarheidsdatumtijd: undefined,
        },
      };

      try {
        await metadata_api.updateMetadata(_newForm as DocumentMetadata, pid);
      } catch (error: unknown) {
        ReducerErrorToaster(thunkAPI, toError(error));
        thunkAPI.dispatch(changeFormIndex({ index: i, setIndex: 0 }));
      }
    }

    thunkAPI.dispatch(
      setToast({
        autoClose: true,
        type: "success",
        message: {
          message: "Document(en) aangeleverd.",
        },
      })
    );
  },
  {
    condition: (_, { getState }) => !(getState() as RootState).meta.fetching,
  }
);

const fetchMetaForId = async (thunkAPI: any, api: IDocumentMetadataApi, id: string): Promise<DocumentMetadata> => {
  try {
    return await api.fetchMetadata(id);
  } catch (error: unknown) {
    ReducerErrorToaster(thunkAPI, toError(error));
    throw error;
  }
};

const fetchAndAddBijlage = async (
  thunkAPI: any,
  api: IDocumentMetadataApi,
  bijlage: Documentrelaties,
  hoofddocId: string
): Promise<void> => {
  const rel_pid = stringSanitizer.extractIdentifierFromPid(bijlage.relation);
  if (!rel_pid) return;

  try {
    const bijlageMeta = await api.fetchMetadata(rel_pid, hoofddocId);
    thunkAPI.dispatch(_addForm({ meta: bijlageMeta }));
  } catch (error: unknown) {
    ReducerErrorToaster(thunkAPI, toError(error));
  }
};

export const fetchForm = createAsyncThunk("metadata/get", async (id: string, thunkAPI) => {
  if (!id) {
    const error = new Error("Id niet gevonden.");
    ReducerErrorToaster(thunkAPI, error);
    throw error;
  }

  const api = ApiFactory.createDocumentMetadataApi();
  const meta = await fetchMetaForId(thunkAPI, api, id);

  thunkAPI.dispatch(clearDocumentState());
  const bijlage_relaties: Documentrelaties[] = await getBijlagenRelatiesEnSetForm(thunkAPI, api, meta);

  const hoofddocId = stringSanitizer.extractIdentifierFromPid(getHoofdDocRelatie(meta.documentrelaties ?? []) ?? id);

  for (const bijlage of bijlage_relaties) {
    await fetchAndAddBijlage(thunkAPI, api, bijlage, hoofddocId);
  }

  thunkAPI.dispatch(setIndexByPid(id));
});

const validateUnpublishArgs = (thunkAPI: any, args: { id: string; reden: string; hoofddocumentid?: string }): void => {
  if (!args.id) {
    const error = new Error("Id niet gevonden.");
    ReducerErrorToaster(thunkAPI, error);
    throw error;
  }
  if (!args.reden) {
    const error = new Error("Geen reden van wijzigen opgegeven.");
    ReducerErrorToaster(thunkAPI, error);
    throw error;
  }
};

const findDocumentInState = (thunkAPI: any, pid: string) => {
  const state = thunkAPI.getState() as RootState;
  const matchesPid = (documentPid?: string) =>
    !!documentPid && stringSanitizer.extractIdentifierFromPid(documentPid) === pid;

  return {
    meta: state.meta.data.flat().find((f) => matchesPid(f.meta.document.pid))?.meta,
    searchResult: state.search.data.resultaten.find((r) => matchesPid(r.document.pid)),
  };
};

const hasWeblocatieForPid = (thunkAPI: any, pid: string): boolean => {
  const { meta, searchResult } = findDocumentInState(thunkAPI, pid);
  return !!(meta?.document.weblocatie || searchResult?.document.weblocatie);
};

const findHoofddocumentIdInState = (thunkAPI: any, pid: string): string | undefined => {
  const { meta, searchResult } = findDocumentInState(thunkAPI, pid);
  const relatie = getHoofdDocRelatie(meta?.documentrelaties ?? searchResult?.documentrelaties ?? []);
  return relatie ? stringSanitizer.extractIdentifierFromPid(relatie) : undefined;
};

const depubliceer = async (
  thunkAPI: any,
  pid: string,
  reden: string,
  hasWeblocatie: boolean,
  hoofddocumentId?: string
): Promise<void> => {
  try {
    if (process.env.REACT_APP_OAS_V2 === "true" || hasWeblocatie) {
      await ApiFactory.createDocumentMetadataApi().depubliceerMetadata(pid, reden, hoofddocumentId);
    } else {
      await ApiFactory.createDocumentApi().depubliceerSession(pid, reden);
    }
  } catch (error: unknown) {
    ReducerErrorToaster(thunkAPI, toError(error));
    throw error;
  }
};

export const unpublish = createAsyncThunk(
  "metadata/delete",
  async (
    args: {
      id: string;
      reden: string;
      hoofddocumentId?: string;
    },
    thunkAPI
  ) => {
    validateUnpublishArgs(thunkAPI, args);

    const pid = stringSanitizer.extractIdentifierFromPid(args.id);
    const hasWeblocatie = hasWeblocatieForPid(thunkAPI, pid);
    const hoofddocumentId = args.hoofddocumentId ?? findHoofddocumentIdInState(thunkAPI, pid);

    await depubliceer(thunkAPI, pid, args.reden, hasWeblocatie, hoofddocumentId);

    thunkAPI.dispatch(
      setToast({
        autoClose: true,
        type: "success",
        message: {
          message: "Document is gedepubliceerd",
        },
      })
    );

    return args.id;
  }
);

export const addForm = createAsyncThunk("metadata/addForm", (_arg, thunkAPI) => {
  const state: any = thunkAPI.getState();
  const formState: MetadataState = state.meta;
  const org = state.auth.organisatie[0];
  const bijlage = generateMetadata("", org);

  // if edit mode
  if (formState.data.length > 0) {
    // Haal hoofdock op
    const { meta } = formState.data[formState.setIndex][0];

    // Kopieer velden
    bijlage.document.publisher = meta.document.publisher;
    bijlage.document.publicatiebestemming = meta.document.publicatiebestemming;
    bijlage.document.classificatiecollectie.informatiecategorieen =
      meta.document.classificatiecollectie.informatiecategorieen;
    bijlage.document.aggregatiekenmerk = meta.document.aggregatiekenmerk;

    // Voeg relatie naar het hoofddocument in bijlage
    if (formState.mode === "update")
      bijlage.documentrelaties = [
        {
          role: BIJLAGE_VAN,
          relation: stringSanitizer.extractIdentifierFromPid(meta.document.pid),
        },
      ];
  }

  thunkAPI.dispatch(
    _addForm({
      meta: bijlage,
    })
  );
});

export const removeForm = createAsyncThunk("metadata/removeForm", (index: number, thunkAPI) => {
  thunkAPI.dispatch(_removeForm(index));
});

const isNotIngetrokken = (element: DocumentMetadata | SearchResult) => {
  return (
    element.plooiIntern?.publicatiestatus === PUBLICATIE_STATUS_SEARCH.ingetrokken ||
    element.plooiIntern?.publicatiestatus === PUBLICATIE_STATUS.ingetrokken
  );
};

const automatischVullenBijlage = (hoofdDoc: FormState, bijlage: FormState) => {
  if (bijlage.meta.document.pid) return;

  const bijlagePrefillEnabled = process.env.REACT_APP_BIJLAGE_PREFILL === "true";

  if (hoofdDoc.meta.document.zichtbaarheidsdatumtijd) {
    const diff = new Date().getTime() - new Date(Date.parse(hoofdDoc.meta.document.zichtbaarheidsdatumtijd)).getTime();
    if (hoofdDoc.meta.plooiIntern?.publicatiestatus === PUBLICATIE_STATUS.uitgesteld || diff < 0)
      bijlage.meta.document.zichtbaarheidsdatumtijd = hoofdDoc.meta.document.zichtbaarheidsdatumtijd;
  }

  bijlage.meta.document.classificatiecollectie.onderwerpenRonl =
    hoofdDoc.meta.document.classificatiecollectie.onderwerpenRonl;
  bijlage.meta.document.classificatiecollectie.themas = hoofdDoc.meta.document.classificatiecollectie.themas;
  if (bijlagePrefillEnabled) {
    bijlage.meta.document.classificatiecollectie.informatiecategorieen =
      hoofdDoc.meta.document.classificatiecollectie.informatiecategorieen;
  }
  bijlage.meta.document.medeverantwoordelijken = hoofdDoc.meta.document.medeverantwoordelijken;
};

const markDocumentAsIngetrokken = (documents: FormState[], targetPid: string) => {
  if (documents === undefined || documents.length === 0) return;

  const targetIndex = documents.findIndex((item) => item.meta.document.pid === targetPid);

  if (targetIndex === -1) return;

  const updateStatus = (item: FormState) => {
    item.meta.plooiIntern = {
      publicatiestatus: PUBLICATIE_STATUS.ingetrokken,
    };
  };

  const isHoofdDocument = targetIndex === 0;

  if (isHoofdDocument) {
    documents.forEach(updateStatus);
  } else {
    updateStatus(documents[targetIndex]);
  }
};

const updateVersiesBestandsnaam = (activeForm: FormState, filename: string): void => {
  const bestanden = [{ bestandsnaam: filename, label: "file" }];

  if (activeForm.meta.versies?.length) {
    activeForm.meta.versies[0].bestanden = bestanden;
  } else {
    activeForm.meta.versies = [{ bestanden }];
  }
};

const metadataState = createSlice({
  name: "metadata",
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<"update" | "create">) => {
      state.mode = action.payload;
    },

    updateNewBijlage: (state, action: PayloadAction<{ index: number; pid: string; fileUploadFailed?: boolean }>) => {
      let doc = state.data[state.setIndex][action.payload.index];
      doc.meta.document.pid = action.payload.pid;

      if (!action.payload.fileUploadFailed) {
        doc.file = undefined;
        doc.hasChangedFile = false;
      }

      const now = new Date();
      const nowString = `${new Date().toLocaleDateString("NL-nl")} ${now.toLocaleTimeString()}`;

      doc.meta.document.creatiedatum = nowString;
    },

    _updateBijlageRelatie: (state, action: PayloadAction<{ index: number; pid: string }>) => {
      state.data[state.setIndex][action.payload.index].meta.documentrelaties = [
        {
          relation: stringSanitizer.extractIdentifierFromPid(action.payload.pid),
          role: BIJLAGE_VAN,
        },
      ];
    },

    _updateHasChanged: (state, action: PayloadAction<{ index: number; hasChanged: boolean }>) => {
      state.data[state.setIndex][action.payload.index].hasChangedMeta = action.payload.hasChanged;
      state.data[state.setIndex][action.payload.index].hasChangedFile = action.payload.hasChanged;
    },

    updateBewerktijd: (state, action: PayloadAction<number>) => {
      if (state.data[state.setIndex][action.payload].meta.versies?.length) {
        state.data[state.setIndex][action.payload].meta.versies![0].mutatiedatumtijd = new Date(Date.now()).toJSON();
      } else {
        state.data[state.setIndex][action.payload].meta.versies = [
          {
            mutatiedatumtijd: new Date(Date.now()).toJSON(),
          },
        ];
      }
    },

    addFormSet: (state) => {
      const document = generateMetadata(
        "",
        state.data[state.setIndex][0].meta.document.publisher as IdentifierMetAfkorting
      );
      state.data.push([{ meta: document }]);
      state.formIndex = 0;
      state.setIndex = state.data.length - 1;
    },

    _addForm: (state, action: PayloadAction<FormState>) => {
      if (!state.data[state.setIndex]) {
        state.data[state.setIndex] = [];
      }

      const form = {
        ...action.payload,
        originalWeblocatieUrl: action.payload.meta.document.weblocatie,
      };
      state.data?.[state.setIndex].push(form);
      state.formUniqueKey += 1;

      if (state.data?.[state.setIndex].length > 1) {
        const hoofdDoc = state.data[state.setIndex][0];
        const bijlage = state.data[state.setIndex][state.data[state.setIndex].length - 1];
        automatischVullenBijlage(hoofdDoc, bijlage);
      }
    },

    _removeForm: (state, action: PayloadAction<number>) => {
      state.documentError.indexList = state.documentError.indexList?.filter(
        (errorIndex) => errorIndex.index !== action.payload
      );
      const index = action.payload;
      const setIndex = state.setIndex;

      if (index === 0 && setIndex === 0 && state.data.length === 1) {
        const publisher = state.data[setIndex][0].meta.document.publisher as IdentifierMetAfkorting;

        const document = generateMetadata("", publisher);
        state.data[setIndex] = [{ meta: document }];
        return;
      }

      if (index === 0) {
        state.data.splice(setIndex, 1);
        state.setIndex = 0;
        return;
      }

      state.data[setIndex].splice(index, 1);

      if (state.data.length <= state.formIndex) {
        state.formIndex = state.data.length - 1;
      }
    },

    clearDocumentState: (state) => {
      state.formUniqueKey += 1;
      state.reasons = [];
      state.data = [];
      state.formIndex = 0;
      state.documentError = {};
      state.fetching = false;
      state.errorReviewed = false;
      state.localValidationError = false;
    },

    changeFormIndex: (state, action: PayloadAction<{ index: number; setIndex: number }>) => {
      const indexChanged = state.formIndex !== action.payload?.index || state.setIndex !== action.payload?.setIndex;
      state.formIndex = action.payload?.index;
      state.setIndex = action.payload?.setIndex;
      if (indexChanged) state.formUniqueKey += 1;
    },

    addReason: (state, action: PayloadAction<{ pid: string; reasonId: string }>) => {
      const { pid, reasonId } = action.payload;
      const existing = state.reasons.find((reason) => reason.pid === pid);
      if (existing) existing.reasonId = reasonId;
      else state.reasons.push({ pid, reasonId });
    },

    removeFile: (state, action: PayloadAction<number>) => {
      state.data[state.setIndex][action.payload].file = undefined;
      state.data[state.setIndex][action.payload].fileExplicitlyRemoved = false;
      state.data[state.setIndex][action.payload].hasChangedFile = false;
    },

    cancelFileChange: (state, action: PayloadAction<CancelFileChangePayload>) => {
      const activeForm = state.data[state.setIndex][state.formIndex];
      activeForm.file = undefined;
      activeForm.hasChangedFile = false;
      activeForm.hasChangedMeta = action.payload.hasChangedMetaVoorWijziging;
      activeForm.meta.document.weblocatie = action.payload.weblocatieVoorWijziging;

      if (activeForm.meta.versies?.length) {
        activeForm.meta.versies[0].bestanden = action.payload.bestandenVoorWijziging;
      }

      state.reasons = state.reasons.filter((reason) => reason.pid !== activeForm.meta.document.pid);
    },

    updateWeblocatie: (state, action: PayloadAction<string | undefined>) => {
      const activeForm = state.data[state.setIndex][state.formIndex];
      activeForm.meta.document.weblocatie = action.payload;
      activeForm.hasChangedFile = true;
      if (action.payload) {
        activeForm.hasChangedMeta = true;
        activeForm.file = undefined;
      }

      if (action.payload === activeForm.originalWeblocatieUrl) {
        state.reasons = state.reasons.filter((element) => activeForm.meta.document.pid !== element.pid);
        activeForm.hasChangedFile = false;
      }
    },

    clearReasons: (state) => {
      state.reasons = [];
    },

    setIndexByPid: (state, action: PayloadAction<string>) => {
      const index = state.data[state.setIndex].findIndex(
        (formState: FormState) => formState.meta.document.pid!.indexOf(action.payload) > -1
      );
      if (index !== -1) state.formIndex = index;
      else state.formIndex = 0;
    },

    updateGeldigheidEinddatum: (state, action: PayloadAction<Date | undefined>) => {
      if (!state.data[state.setIndex][state.formIndex].meta.document.geldigheid)
        state.data[state.setIndex][state.formIndex].meta.document.geldigheid = {};

      state.data[state.setIndex][state.formIndex].meta.document.geldigheid!.einddatum = action.payload
        ? action.payload.toJSON()
        : undefined;

      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateGeldigheidBegindatum: (state, action: PayloadAction<Date | undefined>) => {
      if (!state.data[state.setIndex][state.formIndex].meta.document.geldigheid)
        state.data[state.setIndex][state.formIndex].meta.document.geldigheid = {};

      state.data[state.setIndex][state.formIndex].meta.document.geldigheid!.begindatum = action.payload
        ? action.payload.toJSON()
        : undefined;

      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateDocumentSoorten: (state, action: PayloadAction<Identifier>) => {
      if (
        state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.documentsoorten?.at(0)?.id ===
        action.payload.id
      )
        state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.documentsoorten = undefined;
      else
        state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.documentsoorten = [
          action.payload,
        ];

      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateDocumentHandelingOrganisatie: (state, action: PayloadAction<{ identifier: Identifier; index: number }>) => {
      state.data[state.setIndex][state.formIndex].meta.document.documenthandelingen[
        action.payload.index
      ].wasAssociatedWith = action.payload.identifier;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateDocumentHandelingSoort: (state, action: PayloadAction<{ identifier: Identifier; index: number }>) => {
      state.data[state.setIndex][state.formIndex].meta.document.documenthandelingen[
        action.payload.index
      ].soortHandeling = action.payload.identifier;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    removeDocumentHandelingSoort: (state, action: PayloadAction<number>) => {
      state.data[state.setIndex][state.formIndex].meta.document.documenthandelingen.splice(action.payload, 1);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateDocumentHandelingDate: (state, action: PayloadAction<{ date: string; index: number }>) => {
      const datevalue = new Date(action.payload.date).toJSON();
      state.data[state.setIndex][state.formIndex].meta.document.documenthandelingen[action.payload.index].atTime =
        datevalue;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateVerantwoordelijke: (state, action: PayloadAction<Identifier>) => {
      if (state.data[state.setIndex][state.formIndex].meta.document.verantwoordelijke?.id !== action.payload.id)
        state.data[state.setIndex][state.formIndex].meta.document.verantwoordelijke = action.payload;
      else state.data[state.setIndex][state.formIndex].meta.document.verantwoordelijke = undefined;

      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateMedeverantwoordelijken: (state, action: PayloadAction<Identifier>) => {
      state.data[state.setIndex][state.formIndex].meta.document.medeverantwoordelijken = InsertOrRemoveIdentifier(
        state.data[state.setIndex][state.formIndex].meta.document.medeverantwoordelijken,
        action.payload
      );
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateOmschrijving: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.omschrijvingen = removeEmpty([action.payload]);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    markFileRemoved: (state) => {
      const activeForm = state.data?.[state.setIndex]?.[state.formIndex];
      activeForm.file = undefined;
      activeForm.meta.document.weblocatie = undefined;
      activeForm.hasChangedFile = true;
      activeForm.hasChangedMeta = true;
      activeForm.fileExplicitlyRemoved = true;
    },

    updateFile: (state, action: PayloadAction<File | undefined>) => {
      const activeForm = state.data?.[state.setIndex]?.[state.formIndex];
      activeForm.file = action.payload;
      activeForm.hasChangedFile = true;

      if (!action.payload) return;

      activeForm.meta.document.weblocatie = undefined;
      activeForm.fileExplicitlyRemoved = false;

      if (activeForm.meta.document.titelcollectie.officieleTitel.length === 0) {
        activeForm.meta.document.titelcollectie.officieleTitel = stringSanitizer.removeExtension(action.payload.name);
        activeForm.hasChangedMeta = true;
      }

      updateVersiesBestandsnaam(activeForm, action.payload.name);
    },

    updateThemas: (state, action: PayloadAction<Identifier>) => {
      state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.themas =
        InsertOrRemoveIdentifier(
          state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.themas,
          action.payload
        );
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateOnderwerpenRonl: (state, action: PayloadAction<Identifier>) => {
      state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.onderwerpenRonl =
        InsertOrRemoveIdentifier(
          state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.onderwerpenRonl,
          action.payload
        );
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    setOnderwerpenRonl: (state, action: PayloadAction<Identifier[]>) => {
      state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.onderwerpenRonl = action.payload;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateDocumentHandelingen: (state, action: PayloadAction<Documenthandelingen[]>) => {
      state.data[state.setIndex][state.formIndex].meta.document.documenthandelingen = action.payload;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateIdentifiers: (state, action: PayloadAction<string[]>) => {
      state.data[state.setIndex][state.formIndex].meta.document.identifiers = action.payload;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateOnderwerpen: (state, action: PayloadAction<string[]>) => {
      state.data[state.setIndex][state.formIndex].meta.document.onderwerpen = removeEmpty(action.payload);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateVerkorteTitel: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.titelcollectie.verkorteTitels = removeEmpty([
        action.payload,
      ]);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateAltTitel: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.titelcollectie.alternatieveTitels = removeEmpty([
        action.payload,
      ]);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateTitel: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.titelcollectie.officieleTitel = action.payload;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updatePublicatiebestemming: (state, action: PayloadAction<string | undefined>) => {
      const waarde = action.payload ? [action.payload] : [];
      state.data[state.setIndex][state.formIndex].meta.document.publicatiebestemming = waarde;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;

      for (let data of state.data[state.setIndex]) {
        data.meta.document.publicatiebestemming = waarde;
        data.hasChangedMeta = true;
      }
    },

    updatePublisher: (state, action: PayloadAction<Identifier>) => {
      for (let data of state.data[state.setIndex]) {
        if (
          data.meta.document.verantwoordelijke &&
          data.meta.document.publisher &&
          data.meta.document.verantwoordelijke!.id === data.meta.document.publisher!.id
        ) {
          data.meta.document.verantwoordelijke = action.payload;
        }

        data.meta.document.publisher = action.payload;
        data.hasChangedMeta = true;
      }
    },

    updateOpsteller: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.naamOpsteller = removeEmpty(action.payload);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateAggregatieKenmerk: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.aggregatiekenmerk = removeEmpty(action.payload);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateCreatieDatum: (state, action: PayloadAction<string>) => {
      state.data[state.setIndex][state.formIndex].meta.document.creatiedatum = removeEmpty(action.payload);
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateTaal: (state, action: PayloadAction<Identifier>) => {
      state.data[state.setIndex][state.formIndex].meta.document.language = action.payload;
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateInformatiecategorieen: (state, action: PayloadAction<Identifier>) => {
      state.data[state.setIndex][state.formIndex].meta.document.classificatiecollectie.informatiecategorieen = [
        action.payload,
      ];
      state.data[state.setIndex][state.formIndex].hasChangedMeta = true;
    },

    updateZichtbaarheidDate: (state, action: PayloadAction<string | undefined>) => {
      if (!action.payload || action.payload === undefined) {
        state.data[state.setIndex].forEach((element: FormState) => {
          if (!isNotIngetrokken(element.meta)) {
            element.meta.document.zichtbaarheidsdatumtijd = action.payload;
            element.hasChangedMeta = true;
          }
        });

        return;
      }

      let newDate = stringToDate(action.payload) ?? new Date();
      const zdt = state.data[state.setIndex][state.formIndex].meta.document.zichtbaarheidsdatumtijd;
      if (zdt) {
        const date = new Date(zdt);
        newDate.setHours(date.getHours());
        newDate.setMinutes(date.getMinutes());
      }

      if (newDate < new Date()) {
        newDate = new Date();
        newDate.setMinutes(newDate.getMinutes() + 15);
      }

      state.data[state.setIndex].forEach((element: FormState) => {
        if (!isNotIngetrokken(element.meta)) {
          element.meta.document.zichtbaarheidsdatumtijd = newDate.toJSON();
          element.hasChangedMeta = true;
        }
      });
    },

    updateZichtbaarheidTime: (state, action: PayloadAction<string>) => {
      let newDate = new Date();

      const zdt = state.data[state.setIndex][state.formIndex].meta.document.zichtbaarheidsdatumtijd;
      if (zdt) {
        const date = new Date(zdt);
        newDate.setFullYear(date.getFullYear());
        newDate.setMonth(date.getMonth());
        newDate.setDate(date.getDate());
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
      }

      const s = action.payload!.split(":");
      newDate.setHours(Number(s[0]));
      newDate.setMinutes(Number(s[1]));
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);

      if (newDate < new Date()) return;

      state.data[state.setIndex].forEach((element) => {
        if (!isNotIngetrokken(element.meta)) {
          element.meta.document.zichtbaarheidsdatumtijd = newDate.toJSON();
          element.hasChangedMeta = true;
        }
      });
    },

    updateErrors: (state, action: PayloadAction<{ index: number; setIndex: number }[] | undefined>) => {
      if (action.payload) state.documentError.indexList = action.payload;
      else state.documentError.indexList = [];
    },

    updateLocalValidationError: (state, action: PayloadAction<boolean>) => {
      state.localValidationError = action.payload;
    },

    updateErrorPid: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        if (!state.documentError.pid) state.documentError.pid = [];

        state.documentError.pid?.push(stringSanitizer.extractIdentifierFromPid(action.payload));
      } else {
        state.documentError.pid = [];
      }
    },

    updateErrorReviewed: (state, action: PayloadAction<boolean>) => {
      state.errorReviewed = action.payload;
    },

    updatePid: (state, action: PayloadAction<{ pid: string; index: number }>) => {
      state.data[state.setIndex][action.payload.index].meta.document.pid = action.payload.pid;
    },

    updateExtraMetadata: (state, action: PayloadAction<{ velden: Velden[] }>) => {
      const element = state.data[state.setIndex][state.formIndex];
      element.meta.document.extraMetadata = [{ velden: action.payload.velden }];
      element.hasChangedMeta = true;
    },
  },
  extraReducers: (caseBuilder) => {
    caseBuilder.addCase(create.pending, (state, action) => {
      state.fetching = true;
    });
    caseBuilder.addCase(create.rejected, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(create.fulfilled, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(fetchForm.pending, (state, action) => {
      state.fetching = true;
    });
    caseBuilder.addCase(fetchForm.rejected, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(fetchForm.fulfilled, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(update.pending, (state, action) => {
      state.fetching = true;
    });
    caseBuilder.addCase(update.rejected, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(update.fulfilled, (state, action) => {
      state.fetching = false;
      state.data[state.setIndex].forEach((formState: FormState) => {
        formState.hasChangedFile = false;
        formState.hasChangedMeta = false;
        formState.originalWeblocatieUrl = formState.meta.document.weblocatie;
      });

      if (state.data[state.setIndex][state.formIndex].meta.versies)
        state.data[state.setIndex][state.formIndex].meta.versies![0].mutatiedatumtijd = new Date().toJSON();
    });
    caseBuilder.addCase(publiceerConcept.pending, (state, action) => {
      state.fetching = true;
    });
    caseBuilder.addCase(publiceerConcept.rejected, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(publiceerConcept.fulfilled, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(unpublish.pending, (state, action) => {
      state.fetching = true;
    });
    caseBuilder.addCase(unpublish.rejected, (state, action) => {
      state.fetching = false;
    });
    caseBuilder.addCase(unpublish.fulfilled, (state, action: PayloadAction<string>) => {
      state.fetching = false;
      markDocumentAsIngetrokken(state.data[state.setIndex], action.payload);
    });
  },
});

const {
  _addForm,
  _removeForm,
  updateNewBijlage,
  removeFile,
  updateBewerktijd,
  _updateBijlageRelatie,
  _updateHasChanged,
} = metadataState.actions;
export const {
  updateAggregatieKenmerk,
  removeDocumentHandelingSoort,
  updateGeldigheidBegindatum,
  updateInformatiecategorieen,
  updateTaal,
  updateCreatieDatum,
  updatePublisher,
  updateOpsteller,
  updateTitel,
  updateAltTitel,
  updateVerkorteTitel,
  updateOnderwerpen,
  updateIdentifiers,
  updateDocumentHandelingen,
  updateOnderwerpenRonl,
  setOnderwerpenRonl,
  updateThemas,
  updateFile,
  updateOmschrijving,
  updateMedeverantwoordelijken,
  updateVerantwoordelijke,
  updateDocumentHandelingDate,
  updateDocumentHandelingSoort,
  updateDocumentHandelingOrganisatie,
  updateErrors,
  updateLocalValidationError,
  updateErrorPid,
  updateErrorReviewed,
  updatePid,
  setMode,
  clearDocumentState,
  changeFormIndex,
  addReason,
  clearReasons,
  setIndexByPid,
  updateGeldigheidEinddatum,
  updateDocumentSoorten,
  updateZichtbaarheidDate,
  updateZichtbaarheidTime,
  updatePublicatiebestemming,
  updateExtraMetadata,
  addFormSet,
  updateWeblocatie,
  cancelFileChange,
  markFileRemoved,
} = metadataState.actions;

export default metadataState.reducer;
